import React, { useMemo } from 'react';
import { Checkbox, MenuItem, Select, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { UseRowConfigProps } from './useRowConfig.props';
import { BulkBacksolveUpdate, BulkValuationUpdate } from '../../../../../../api';
import { dbShortDate, ISO_DATE_FORMAT } from '../../../../../../utilities';
import {
  ADJUSTMENT_SELECTED_FIELD,
  ADJUSTMENT_TYPE_SELECTION_FIELD,
  AS_OF_DATE_FIELD,
  BACKSOLVE_FIELD,
  IS_FIRM_OWNED_FIELD,
  IS_READY_FOR_AUDIT_FIELD,
  MARKET_DATE_FIELD,
  MAX_PERCENT_CHANGE_FIELD,
  MAX_PERCENT_CHANGE_HEADER,
  METRIC_FIELD,
  MIN_PERCENT_CHANGE_FIELD,
  MIN_PERCENT_CHANGE_HEADER,
  OPM_BACKSOLVE_DATE_FIELD,
  STATUS,
  TRANSACTION_COMPS_FIELD,
} from '../../constants';

const useRowConfig = ({
  rows,
  visibleColumns,
  visibleSubColumns,
  latestPossibleMarketDate,
  updateRow,
  isScalarUser,
}: UseRowConfigProps) => {
  const rowConfig = useMemo(
    () =>
      rows.map(prevRow => {
        const {
          id,
          company_name,
          valuation_id,
          market_date,
          status,
          is_ready_for_audit,
          is_firm_owned,
          as_of_date,
          opm_backsolve_date,
          adjustment_type_selection,
          adjustment_selected,
          metric,
          min_percent_change,
          max_percent_change,
        } = prevRow;
        return {
          id,
          company_name,
          valuation_id,
          market_date,
          status,
          is_ready_for_audit,
          is_firm_owned,
          as_of_date,
          opm_backsolve_date,
          adjustment_type_selection,
          adjustment_selected,
          metric,
          min_percent_change,
          max_percent_change,
          rowCells: [
            {
              field: MARKET_DATE_FIELD,
              renderCell: () => (
                <DatePicker
                  value={market_date ? moment(market_date, ISO_DATE_FORMAT).toDate() : null}
                  format="MM/dd/yyyy"
                  maxDate={latestPossibleMarketDate.toDate()}
                  views={['year', 'month', 'day']}
                  slotProps={{
                    textField: {
                      variant: 'standard',
                    },
                  }}
                  onChange={date => {
                    const formattedDate = date ? dbShortDate(date) : '';
                    updateRow(Number(id), MARKET_DATE_FIELD, formattedDate);
                  }}
                />
              ),
              isVisible: visibleColumns.includes('market_date'),
            },
            {
              field: STATUS,
              renderCell: () => (
                <Select
                  variant="standard"
                  value={status}
                  labelId={`status-select-label-${id}`}
                  id={`status-select-${id}`}
                  onChange={event => {
                    updateRow(Number(id), 'status', event.target.value);

                    if (event.target.value === BulkValuationUpdate.status.DRAFT) {
                      updateRow(Number(id), IS_READY_FOR_AUDIT_FIELD, false);
                    }
                  }}>
                  {Object.values(BulkValuationUpdate.status).map(status => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              ),
              isVisible: visibleColumns.includes(STATUS),
            },
            {
              field: TRANSACTION_COMPS_FIELD,
              renderCell: () => (
                <Checkbox
                  defaultChecked
                  onChange={(_, checked) => {
                    updateRow(Number(id), TRANSACTION_COMPS_FIELD, checked)
                  }}
                />
              ),
              isVisible: visibleColumns.includes(TRANSACTION_COMPS_FIELD),
            },
            {
              field: IS_FIRM_OWNED_FIELD,
              renderCell: () => {
                const numberValue = Number(is_firm_owned);
                return (
                  <Select
                    variant="standard"
                    value={numberValue}
                    labelId={`owner-select-label-${id}`}
                    disabled={!isScalarUser}
                    id={`owner-select-${id}`}
                    onChange={event => updateRow(Number(id), IS_FIRM_OWNED_FIELD, !!event.target.value)}>
                    <MenuItem key="firm-valuation" value={1}>
                      Firm Valuation
                    </MenuItem>
                    <MenuItem key="scalar-valuation" value={0}>
                      Scalar Valuation
                    </MenuItem>
                  </Select>
                );
              },
              isVisible: visibleColumns.includes(IS_FIRM_OWNED_FIELD),
            },
            {
              field: IS_READY_FOR_AUDIT_FIELD,
              renderCell: () => {
                const numberValue = Number(is_ready_for_audit);
                return (
                  <Select
                    variant="standard"
                    value={numberValue}
                    labelId={`audit-select-label-${id}`}
                    disabled={status === BulkValuationUpdate.status.DRAFT}
                    id={`audit-select-${id}`}
                    onChange={event => updateRow(Number(id), IS_READY_FOR_AUDIT_FIELD, !!event.target.value)}>
                    <MenuItem key="ready-for-audit" value={1}>
                      Ready for Audit
                    </MenuItem>
                    <MenuItem key="not-ready-for-audit" value={0}>
                      Not Ready for Audit
                    </MenuItem>
                  </Select>
                );
              },
              isVisible: visibleColumns.includes(IS_READY_FOR_AUDIT_FIELD),
            },
            {
              field: AS_OF_DATE_FIELD,
              renderCell: () => (
                <DatePicker
                  slotProps={{
                    textField: {
                      variant: 'standard',
                    },
                  }}
                  value={as_of_date ? moment(as_of_date, ISO_DATE_FORMAT).toDate() : null}
                  format="MM/dd/yyyy"
                  views={['year', 'month', 'day']}
                  maxDate={latestPossibleMarketDate.toDate()}
                  onChange={date => {
                    const formattedDate = date ? dbShortDate(date) : '';
                    updateRow(Number(id), AS_OF_DATE_FIELD, formattedDate);
                  }}
                />
              ),
              isVisible:
                visibleColumns.includes(BACKSOLVE_FIELD) &&
                visibleSubColumns.find(
                  subCol => subCol.field === AS_OF_DATE_FIELD && subCol.parentField === BACKSOLVE_FIELD
                ) !== undefined,
            },
            {
              field: OPM_BACKSOLVE_DATE_FIELD,
              renderCell: () => (
                <DatePicker
                  slotProps={{
                    textField: {
                      variant: 'standard',
                    },
                  }}
                  value={opm_backsolve_date ? moment(opm_backsolve_date, ISO_DATE_FORMAT).toDate() : null}
                  format="MM/dd/yyyy"
                  views={['year', 'month', 'day']}
                  maxDate={latestPossibleMarketDate.toDate()}
                  onChange={date => {
                    const formattedDate = date ? dbShortDate(date) : '';
                    updateRow(Number(id), OPM_BACKSOLVE_DATE_FIELD, formattedDate);
                  }}
                />
              ),
              isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
            },
            {
              isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
              field: ADJUSTMENT_TYPE_SELECTION_FIELD,
              renderCell: () => (
                <Select
                  variant="standard"
                  value={adjustment_type_selection}
                  labelId={`${ADJUSTMENT_TYPE_SELECTION_FIELD}-${id}`}
                  id={`${ADJUSTMENT_TYPE_SELECTION_FIELD}-${id}`}
                  onChange={event => updateRow(Number(id), ADJUSTMENT_TYPE_SELECTION_FIELD, event.target.value)}>
                  {Object.values(BulkBacksolveUpdate.adjustment_type_selection).map(adjustmentType => (
                    <MenuItem key={adjustmentType} value={adjustmentType}>
                      {adjustmentType}
                    </MenuItem>
                  ))}
                </Select>
              ),
            },
            {
              isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
              field: METRIC_FIELD,
              renderCell: () => (
                <Select
                  variant="standard"
                  value={metric}
                  labelId={`${METRIC_FIELD}-${id}`}
                  id={`${METRIC_FIELD}-${id}`}
                  onChange={event => updateRow(Number(id), METRIC_FIELD, event.target.value)}>
                  {Object.values(BulkBacksolveUpdate.metric).map(value => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              ),
            },
            {
              isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
              field: ADJUSTMENT_SELECTED_FIELD,
              renderCell: () => (
                <Select
                  variant="standard"
                  value={adjustment_selected}
                  labelId={`${ADJUSTMENT_SELECTED_FIELD}-${id}`}
                  id={`${ADJUSTMENT_SELECTED_FIELD}-${id}`}
                  onChange={event => updateRow(Number(id), ADJUSTMENT_SELECTED_FIELD, event.target.value)}>
                  {Object.values(BulkBacksolveUpdate.adjustment_selected).map(percentileLabel => (
                    <MenuItem key={percentileLabel} value={percentileLabel}>
                      {percentileLabel}
                    </MenuItem>
                  ))}
                </Select>
              ),
            },
            {
              field: MIN_PERCENT_CHANGE_FIELD,
              isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
              renderCell: () => (
                <TextField
                  type="number"
                  value={min_percent_change || ''}
                  onChange={event => updateRow(Number(id), MIN_PERCENT_CHANGE_FIELD, event.target.value)}
                  placeholder={MIN_PERCENT_CHANGE_HEADER}
                  variant="standard"
                  size="small"
                />
              ),
            },
            {
              field: MAX_PERCENT_CHANGE_FIELD,
              isVisible: visibleColumns.includes(BACKSOLVE_FIELD),
              renderCell: () => (
                <TextField
                  type="number"
                  value={max_percent_change || ''}
                  onChange={event => updateRow(Number(id), MAX_PERCENT_CHANGE_FIELD, event.target.value)}
                  placeholder={MAX_PERCENT_CHANGE_HEADER}
                  variant="standard"
                  size="small"
                />
              ),
            },
          ],
        };
      }),
    [rows, visibleColumns, latestPossibleMarketDate, updateRow, isScalarUser, visibleSubColumns]
  );

  return rowConfig;
};

export default useRowConfig;
